import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useIntl } from 'react-intl';
import api from '../api';
import { store } from '../store';

interface Interaction {
  id: string;
  author: string;
  avatar: string;
  created_at: string;
  warning: string | null;
  text: string | null;
}
interface Props {
  accountId: string;
  anchor: DOMRect;
  id: string;
  onEnter: () => void;
  onLeave: () => void;
}

export const AvatarConversation = ({ accountId, anchor, id, onEnter, onLeave }: Props) => {
  const intl = useIntl();
  const [result, setResult] = useState<Interaction[] | undefined>(undefined);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    const controller = new AbortController();
    setResult(undefined);
    setFailed(false);
    void api(store.getState).get<{ interactions: Interaction[] }>(`/api/v1/accounts/${accountId}/latest_interaction`, { signal: controller.signal })
      .then(({ data }) => { if (!controller.signal.aborted) setResult(data.interactions); })
      .catch(() => { if (!controller.signal.aborted) setFailed(true); });
    return () => controller.abort();
  }, [accountId]);

  const width = Math.min(320, window.innerWidth - 24);
  const left = Math.max(12, Math.min(anchor.left + anchor.width / 2 - width / 2, window.innerWidth - width - 12));
  const belowSpace = window.innerHeight - anchor.bottom - 24;
  const aboveSpace = anchor.top - 24;
  const above = belowSpace < 360 && aboveSpace > belowSpace;
  const maxHeight = Math.max(80, Math.min(380, above ? aboveSpace : belowSpace));
  const tailLeft = Math.max(18, Math.min(width - 18, anchor.left + anchor.width / 2 - left));
  return createPortal(
    <div id={id} role='tooltip' className={`avatar-conversation${above ? ' avatar-conversation--above' : ''}`} onMouseEnter={onEnter} onMouseLeave={onLeave}
      style={{ width, left, '--tail-left': `${tailLeft}px`, ...(above ? { bottom: Math.max(12, window.innerHeight - anchor.top + 10) } : { top: anchor.bottom + 10 }) } as React.CSSProperties}>
      <div className='avatar-conversation__body' style={{ maxHeight }}>
      <div className='avatar-conversation__title'>{intl.formatMessage({ id: 'avatar_conversation.title', defaultMessage: 'Your most recent conversation' })}</div>
      {failed ? <p>{intl.formatMessage({ id: 'avatar_conversation.error', defaultMessage: 'Could not load the conversation.' })}</p> : result === undefined ?
        <p>{intl.formatMessage({ id: 'avatar_conversation.loading', defaultMessage: 'Loading…' })}</p> : result.length === 0 ?
        <p>{intl.formatMessage({ id: 'avatar_conversation.empty', defaultMessage: 'No mentions or replies between you yet.' })}</p> : result.map(item => <div className='avatar-conversation__item' key={item.id}>
          <img className='avatar-conversation__avatar' src={item.avatar} alt='' width={32} height={32} />
          <div className='avatar-conversation__content'>
          <div className='avatar-conversation__meta'><strong>{item.author}</strong><time dateTime={item.created_at}>{intl.formatDate(item.created_at, { month: 'short', day: 'numeric', hour: 'numeric', minute: 'numeric' })}</time></div>
          <p>{item.warning ? `${intl.formatMessage({ id: 'avatar_conversation.warning', defaultMessage: 'Content warning' })}: ${item.warning}` : item.text || intl.formatMessage({ id: 'avatar_conversation.media', defaultMessage: 'A post with media or a poll.' })}</p>
          </div>
        </div>)}
      </div>
    </div>, document.body,
  );
};
